<script setup lang="ts">
/**
 * Slim modal on Bootstrap's modal CSS (no Bootstrap JS): teleported to <body>, focus moves into
 * the dialog and back to the opener, Tab stays inside, body scroll is locked, Escape and the
 * backdrop close it (unless `persistent`). `fullscreen` = "md-down" → fullscreen on phones.
 */
import { nextTick, onBeforeUnmount, onMounted, ref, useId } from 'vue';

const props = withDefaults(
  defineProps<{
    title?: string;
    size?: 'sm' | 'lg' | 'xl';
    fullscreen?: 'always' | 'sm-down' | 'md-down' | 'lg-down';
    persistent?: boolean;
    /** Escape is handled elsewhere (shortcut service, record scope) */
    noEscape?: boolean;
    scrollable?: boolean;
    dialogClass?: string;
  }>(),
  { scrollable: true },
);
const emit = defineEmits<{ close: [] }>();
defineOptions({ inheritAttrs: false });

const titleId = useId();
const root = ref<HTMLElement | null>(null);
const dialog = ref<HTMLElement | null>(null);
let opener: Element | null = null;

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function close() {
  if (!props.persistent) emit('close');
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && !props.noEscape) {
    e.stopPropagation();
    close();
  } else if (e.key === 'Tab' && dialog.value) {
    const items = [...dialog.value.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!items.length) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

onMounted(async () => {
  opener = document.activeElement;
  document.body.dataset.creatorModals = String(Number(document.body.dataset.creatorModals ?? 0) + 1);
  document.body.classList.add('modal-open');
  await nextTick();
  const auto = dialog.value?.querySelector<HTMLElement>('[autofocus]') ?? dialog.value;
  auto?.focus();
});
onBeforeUnmount(() => {
  const n = Number(document.body.dataset.creatorModals ?? 1) - 1;
  if (n <= 0) {
    delete document.body.dataset.creatorModals;
    document.body.classList.remove('modal-open');
  } else document.body.dataset.creatorModals = String(n);
  if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
});

defineExpose({ root });
</script>

<template>
  <Teleport to="body">
    <div class="modal-backdrop fade show"></div>
    <div ref="root" class="modal fade show d-block" :class="$attrs.class" role="presentation" @mousedown.self="close" @keydown="onKeydown">
      <div
        ref="dialog"
        class="modal-dialog"
        :class="[
          size ? `modal-${size}` : '',
          fullscreen === 'always' ? 'modal-fullscreen' : fullscreen ? `modal-fullscreen-${fullscreen}` : '',
          scrollable ? 'modal-dialog-scrollable' : '',
          dialogClass,
        ]"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title || $slots.title || $slots.header ? titleId : undefined"
        tabindex="-1"
      >
        <div class="modal-content">
          <div v-if="title || $slots.title || $slots.header" class="modal-header gap-2">
            <!-- a custom header puts :id="titleId" on its heading (dialog name) -->
            <slot name="header" :title-id="titleId">
              <h2 :id="titleId" class="modal-title fs-5 text-truncate me-auto"><slot name="title">{{ title }}</slot></h2>
              <slot name="header-actions"></slot>
              <button v-if="!persistent" type="button" class="btn-close ms-0" :aria-label="$t('app.close')" @click="emit('close')"></button>
            </slot>
          </div>
          <div class="modal-body"><slot></slot></div>
          <div v-if="$slots.footer" class="modal-footer"><slot name="footer"></slot></div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
